/**
 * KAN-236: change-guard для Zustand-сторов с серверными данными.
 *
 * Проблема: fetch-экшены (в т.ч. поллинг каждые 20с) вызывают `set({ data: next })`
 * с НОВОЙ ссылкой на каждый тик, даже когда данные не изменились. Zustand сравнивает
 * вывод селектора по `Object.is`, поэтому новая ссылка ⇒ ререндер всех подписчиков
 * впустую (мигания, потеря фокуса, лишний markdown/гиф-рендер).
 *
 * Решение: перед записью сравнить новое значение со старым структурно и, если они
 * равны, сохранить СТАРУЮ ссылку — тогда селекторы не увидят изменения и не ререндерят.
 */

/**
 * Структурное сравнение двух значений (объекты/массивы/примитивы/Date/null).
 * Достаточно для JSON-подобных серверных данных; не поддерживает Map/Set/циклы
 * (в серверных DTO их нет). NaN считается равным NaN (как в Object.is).
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  // NaN === NaN
  if (typeof a === "number" && typeof b === "number") {
    return Number.isNaN(a) && Number.isNaN(b);
  }

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }

  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;

  // Set/Map/иные не-plain объекты (class-инстансы) структурно не сравниваем: у Set/Map
  // нет собственных enumerable-ключей, поэтому наивный обход дал бы ложное "равны".
  // Консервативно возвращаем false (разные ссылки ⇒ считаем изменёнными) — preserveIfEqual
  // тогда просто не сохранит ссылку, что безопасно (максимум лишний ререндер, не пропуск).
  if (!aIsArray) {
    const aProto = Object.getPrototypeOf(a);
    const bProto = Object.getPrototypeOf(b);
    const plain = (p: unknown) => p === Object.prototype || p === null;
    if (!plain(aProto) || !plain(bProto)) return false;
  }

  if (aIsArray && bIsArray) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false;
    if (!deepEqual(aObj[key], bObj[key])) return false;
  }
  return true;
}

/**
 * Возвращает `current`, если он структурно равен `next` (сохраняет ссылку → нет
 * ререндера подписчиков), иначе `next`. Основной примитив change-guard'а.
 *
 * Использование в fetch-экшене:
 *   set({ data: preserveIfEqual(get().data, next), isLoading: false });
 */
export function preserveIfEqual<T>(current: T, next: T): T {
  return deepEqual(current, next) ? current : next;
}
